import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Newspaper, 
  Plus,
  Clock,
  Eye,
  ExternalLink,
  Building2,
  Leaf,
  Users,
  Shield,
  Star,
  Trash2,
  RefreshCw,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "csrd", label: "CSRD", icon: Shield, color: "bg-primary" },
  { value: "environnement", label: "Environnement", icon: Leaf, color: "bg-chart-1" },
  { value: "social", label: "Social", icon: Users, color: "bg-chart-3" },
  { value: "gouvernance", label: "Gouvernance", icon: Building2, color: "bg-chart-5" },
  { value: "interne", label: "Interne", icon: Building2, color: "bg-chart-4" },
];

export default function News() {
  const { token, user } = useAuth();
  const isAuthenticated = !!token;
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "interne",
    image_url: "",
    source_url: ""
  });

  useEffect(() => {
    fetchNews();
    // Auto-refresh every 30 seconds for real-time feel
    const interval = setInterval(fetchNews, 30000);
    return () => clearInterval(interval);
  }, [selectedCategory, token]);

  const fetchNews = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : "";
      // Use public endpoint for non-authenticated users
      const endpoint = isAuthenticated ? `${API}/news${params}` : `${API}/news/public${params}`;
      const headers = isAuthenticated ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(endpoint, { headers });
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
      // If error, try public endpoint as fallback
      try {
        const params = selectedCategory ? `?category=${selectedCategory}` : "";
        const response = await axios.get(`${API}/news/public${params}`);
        setNews(response.data);
      } catch (fallbackError) {
        toast.error("Erreur lors du chargement des actualités");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/news`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Actualité publiée !");
      setDialogOpen(false);
      setFormData({
        title: "",
        content: "",
        category: "interne",
        image_url: "",
        source_url: ""
      });
      fetchNews();
    } catch (error) {
      toast.error("Erreur lors de la publication");
    }
  };

  const handleDelete = async (newsId) => {
    try {
      await axios.delete(`${API}/news/${newsId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Actualité supprimée");
      fetchNews();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getCategoryConfig = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return `Il y a ${diffDays}j`;
  };

  // Public (non-authenticated) view
  if (!isAuthenticated) {
    if (loading) {
      return (
        <div className="min-h-screen bg-background">
          <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/">
                  <Button variant="ghost">Accueil</Button>
                </Link>
                <Link to="/vf-actions">
                  <Button variant="ghost">VF ACTIONS</Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost">Connexion</Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full px-6">
                    Commencer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </nav>
          <main className="pt-24 px-6 pb-12">
            <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[50vh]">
              <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
            </div>
          </main>
        </div>
      );
    }

    const featuredNews = news.filter(n => n.is_featured);
    const regularNews = news.filter(n => !n.is_featured);
    const publicCategories = CATEGORIES.filter(c => c.value !== "interne");

    return (
      <div className="min-h-screen bg-background">
        {/* Public Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Leaf className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost">Accueil</Button>
              </Link>
              <Link to="/vf-actions">
                <Button variant="ghost">VF ACTIONS</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost">Connexion</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full px-6">
                  Commencer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Public Content */}
        <main className="pt-24 px-6 pb-12" data-testid="news-page-public">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  Actualités RSE / CSRD
                </h1>
                <p className="text-muted-foreground mt-1">
                  Les dernières actualités en matière de durabilité et conformité CSRD
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchNews(true)}
                disabled={refreshing}
                data-testid="refresh-news-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>

            {/* Category Tabs */}
            <Tabs defaultValue="all" className="space-y-6" onValueChange={(v) => setSelectedCategory(v === "all" ? null : v)}>
              <TabsList className="flex-wrap">
                <TabsTrigger value="all">Toutes</TabsTrigger>
                {publicCategories.map(cat => (
                  <TabsTrigger key={cat.value} value={cat.value}>
                    <cat.icon className="w-4 h-4 mr-2" />
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="all" className="space-y-6">
                {/* Featured News */}
                {featuredNews.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-chart-4" />
                      À la une
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {featuredNews.map(item => (
                        <NewsCard 
                          key={item.id} 
                          news={item} 
                          featured 
                          onDelete={null}
                          userId={null}
                          formatTimeAgo={formatTimeAgo}
                          getCategoryConfig={getCategoryConfig}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Regular News */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularNews.map(item => (
                    <NewsCard 
                      key={item.id} 
                      news={item}
                      onDelete={null}
                      userId={null}
                      formatTimeAgo={formatTimeAgo}
                      getCategoryConfig={getCategoryConfig}
                    />
                  ))}
                </div>

                {news.length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-medium text-foreground mb-2">Aucune actualité</h3>
                      <p className="text-sm text-muted-foreground">
                        Les actualités RSE et CSRD apparaîtront ici
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Category-specific tabs */}
              {publicCategories.map(cat => (
                <TabsContent key={cat.value} value={cat.value} className="space-y-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {news.map(item => (
                      <NewsCard 
                        key={item.id} 
                        news={item}
                        onDelete={null}
                        userId={null}
                        formatTimeAgo={formatTimeAgo}
                        getCategoryConfig={getCategoryConfig}
                      />
                    ))}
                  </div>
                  {news.length === 0 && (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <cat.icon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-medium text-foreground mb-2">Aucune actualité {cat.label}</h3>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              ))}
            </Tabs>

            {/* CTA for registration */}
            <Card className="mt-12 bg-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                  Accédez à toutes les fonctionnalités
                </h3>
                <p className="text-muted-foreground mb-4">
                  Créez un compte pour analyser vos décisions, publier vos actualités internes et suivre vos objectifs RSE
                </p>
                <Link to="/register">
                  <Button className="rounded-full px-8">
                    Créer un compte
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 bg-card border-t border-border">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Leaf className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground">VF Decide</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <Link to="/vf-actions" className="hover:text-primary transition-colors">VF ACTIONS</Link>
              <Link to="/actualites" className="hover:text-primary transition-colors">Actualités</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              2026 VF Decide - Plateforme RSE/CSRD
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Authenticated view with Sidebar
  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  const featuredNews = news.filter(n => n.is_featured);
  const regularNews = news.filter(n => !n.is_featured);

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="news-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Actualités RSE
            </h1>
            <p className="text-muted-foreground mt-1">
              Veille CSRD/RSE et actualités de votre organisation
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchNews(true)}
              disabled={refreshing}
              data-testid="refresh-news-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full px-6" data-testid="new-news-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Publier
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope' }}>Publier une actualité</DialogTitle>
                  <DialogDescription>
                    Partagez une actualité avec votre organisation
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titre</Label>
                    <Input
                      id="title"
                      placeholder="Titre de l'actualité"
                      value={formData.title}
                      onChange={(e) => handleChange("title", e.target.value)}
                      required
                      data-testid="news-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                      <SelectTrigger data-testid="news-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content">Contenu</Label>
                    <Textarea
                      id="content"
                      placeholder="Rédigez votre actualité..."
                      value={formData.content}
                      onChange={(e) => handleChange("content", e.target.value)}
                      rows={5}
                      required
                      data-testid="news-content-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source_url">Lien source (optionnel)</Label>
                    <Input
                      id="source_url"
                      type="url"
                      placeholder="https://..."
                      value={formData.source_url}
                      onChange={(e) => handleChange("source_url", e.target.value)}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="rounded-full" data-testid="submit-news-btn">
                      Publier
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs defaultValue="all" className="space-y-6" onValueChange={(v) => setSelectedCategory(v === "all" ? null : v)}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="all">Toutes</TabsTrigger>
            {CATEGORIES.map(cat => (
              <TabsTrigger key={cat.value} value={cat.value}>
                <cat.icon className="w-4 h-4 mr-2" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="space-y-6">
            {/* Featured News */}
            {featuredNews.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-chart-4" />
                  À la une
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {featuredNews.map(item => (
                    <NewsCard 
                      key={item.id} 
                      news={item} 
                      featured 
                      onDelete={handleDelete}
                      userId={user?.id}
                      formatTimeAgo={formatTimeAgo}
                      getCategoryConfig={getCategoryConfig}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Regular News */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularNews.map(item => (
                <NewsCard 
                  key={item.id} 
                  news={item}
                  onDelete={handleDelete}
                  userId={user?.id}
                  formatTimeAgo={formatTimeAgo}
                  getCategoryConfig={getCategoryConfig}
                />
              ))}
            </div>

            {news.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-foreground mb-2">Aucune actualité</h3>
                  <p className="text-sm text-muted-foreground">
                    Les actualités RSE et CSRD apparaîtront ici
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Category-specific tabs render the same content filtered */}
          {CATEGORIES.map(cat => (
            <TabsContent key={cat.value} value={cat.value} className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {news.map(item => (
                  <NewsCard 
                    key={item.id} 
                    news={item}
                    onDelete={handleDelete}
                    userId={user?.id}
                    formatTimeAgo={formatTimeAgo}
                    getCategoryConfig={getCategoryConfig}
                  />
                ))}
              </div>
              {news.length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <cat.icon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium text-foreground mb-2">Aucune actualité {cat.label}</h3>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}

function NewsCard({ news, featured, onDelete, userId, formatTimeAgo, getCategoryConfig }) {
  const categoryConfig = getCategoryConfig(news.category);
  const CategoryIcon = categoryConfig.icon;

  return (
    <Card className={`card-hover ${featured ? 'border-2 border-chart-4' : ''}`} data-testid={`news-card-${news.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={`${categoryConfig.color} text-white`}>
              <CategoryIcon className="w-3 h-3 mr-1" />
              {categoryConfig.label}
            </Badge>
            {news.is_internal && (
              <Badge variant="outline" className="text-xs">Interne</Badge>
            )}
            {featured && (
              <Star className="w-4 h-4 text-chart-4 fill-chart-4" />
            )}
          </div>
          {news.is_internal && news.author_id === userId && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(news.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        <h3 className="font-semibold text-foreground mb-2 line-clamp-2" style={{ fontFamily: 'Manrope' }}>
          {news.title}
        </h3>
        
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
          {news.summary || news.content.substring(0, 150)}
        </p>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimeAgo(news.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {news.views}
            </span>
          </div>
          {news.source_url && (
            <a 
              href={news.source_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
            >
              Source
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        
        {news.author_name && (
          <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
            Par {news.author_name}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
